import { useEffect, useState } from "react";
import { FaCaretDown, FaCaretUp, FaPlus, FaEdit } from "react-icons/fa";
import Modal from "react-bootstrap/Modal";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

// Assets
import noActivityIcon from "../assets/204-page.png";

// Components
import NotaSpeseFormAllNote from "./NotaSpeseFormAllNote";
import SpendingLineChart from "./charts/SpendingLineChart";
import {
  SchedaHeader,
  NotaSpesaItem,
  ExpensesSummary,
  ShareModal,
  DeleteConfirmModal,
  DropzoneCard,
  ReceiptReviewModal,
  useExpencers,
  useSchedaShare,
  useModalState,
  useTitleEdit,
} from "./shared";

// Redux actions
import {
  deleteSchedaSpese,
  singleSchedaSpeseGet,
  updateSchedaSpese,
} from "../features/schedaSpese/schedaSpeseSlice";
import { deleteNotaSpese } from "../features/notaSpese/notaSpeseSlice";
import { scanAttachment } from "../features/attachments/attachmentSlice";

/**
 * SortButton - Reusable sort button component for the ordering functionality
 */
const SortButton = ({ label, orderKey, orderState, onClick, disabled }) => {
  const isActive = orderState[orderKey] === "asc" || orderState[orderKey] === "desc";
  
  return (
    <button
      type="button"
      className={`sort-toggle me-2 mb-2 ${isActive ? "sort-toggle--active" : ""}`}
      onClick={() => onClick(orderKey)}
      disabled={disabled}
    >
      <span>{label}</span>
      <span className="d-flex flex-column">
        <FaCaretUp size={10} style={{ opacity: orderState[orderKey] === "desc" ? 1 : 0.35 }} />
        <FaCaretDown size={10} style={{ opacity: orderState[orderKey] === "asc" ? 1 : 0.35 }} />
      </span>
    </button>
  );
};

function SingleSchedaAllNoteNew({ scheda, categorie, listScope = "shared" }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);

  // Custom hooks
  const { modalState, handleShow, handleClose } = useModalState();
  const { expencersWithTotals, maxExpencer } = useExpencers(scheda);

  // Local state
  const [isDisabled, setisDisabled] = useState(false);
  const [notaSpesaToEdit, setNotaSpesaToEdit] = useState(null);
  const [refSchedaId, SetRefSchedaId] = useState(null);

  // Receipt scan/review state - the file is already saved (as an
  // Attachment) by the /scan call itself; this just holds the parsed
  // preview until the user reviews it in ReceiptReviewModal.
  const [scanResult, setScanResult] = useState(null);
  const [showReceiptReview, setShowReceiptReview] = useState(false);

  // Sorting state
  const [orderState, setOrderState] = useState({
    userOrder: null,
    categoryOrder: null,
    dateOrder: "desc",
    priceOrder: null,
  });

  // Refresh callback
  const onRefreshSingle = async () => {
    await dispatch(singleSchedaSpeseGet(scheda._id)).unwrap();
  };

  // Title editing hook
  const {
    titolo,
    erroLength,
    isEditing,
    onLongPress,
    onChange,
    cancelEdit,
    updateSchedataTitolo,
  } = useTitleEdit(scheda, onRefreshSingle);

  // Share hook
  const {
    utentiList,
    isFormModified,
    removeSharedUser,
    changeUserRole,
    getPendingRole,
    addEmail,
    validateEmail,
    removeEmail,
    resetForm,
    handleSubmit: editSharedUserSubmit,
  } = useSchedaShare(scheda, onRefreshSingle, () => handleClose("shareModal"));

  // Handlers
  const handleModalClose = (modalType) => {
    handleClose(modalType);
    resetForm();
    if (modalType !== "editNotaModal") setNotaSpesaToEdit(null);
    if (modalType === "deleteNotaModal") setNotaSpesaToEdit(null);
  };

  const editNota = (notaSpesa) => {
    handleShow("editNotaModal");
    setNotaSpesaToEdit(notaSpesa);
  };

  const closeCreateEditModal = (schedaID) => {
    handleClose("editNotaModal");
    handleShow("deleteNotaModal");
    SetRefSchedaId(schedaID);
  };

  const deleteNotaSpeseAction = async (notaId) => {
    const body = {
      notaId,
      schedaId: refSchedaId,
    };

    try {
      const response = await dispatch(deleteNotaSpese(body)).unwrap();
      await dispatch(singleSchedaSpeseGet(body.schedaId)).unwrap();
      handleModalClose("deleteNotaModal");
      toast.success(response.message || "Note succesfull deleted");
    } catch (error) {
      console.log("Error Response:", error);
      toast.error(`Error deleting note: ${error.message}`);
    }
  };

  const deleteSchedaFromDetail = async () => {
    try {
      const response = await dispatch(deleteSchedaSpese(scheda._id)).unwrap();
      toast.success(response.message || "Scheda succesfull deleted");
      if (response?.id) {
        navigate(`/note-spese/${listScope}`);
      }
    } catch (error) {
      console.log("Error Response:", error);
      toast.error(`Error deleting scheda: ${error.message}`);
    } finally {
      handleModalClose("deleteModal");
    }
  };

  // Receipt scan - DropzoneCard's Upload button lands here (single file at a
  // time). Scanning already persists the file server-side regardless of
  // OCR outcome, so a failed/empty read still opens the review modal (it
  // just has nothing to confirm, see ReceiptReviewModal's empty state).
  const handleAttachmentUpload = async (files) => {
    const file = files[0];
    if (!file) return;
    try {
      const result = await dispatch(scanAttachment({ schedaId: scheda._id, file })).unwrap();
      setScanResult({ ...result, filename: file.name });
      setShowReceiptReview(true);
    } catch (error) {
      toast.error(`Error scanning file: ${error}`);
    }
  };

  const handleReceiptReviewHide = async (didConfirm) => {
    setShowReceiptReview(false);
    setScanResult(null);
    if (didConfirm) {
      await onRefreshSingle();
    }
  };

  // Collaborators card kebab menu — applies a single role/removal change
  // immediately (unlike ShareModal's batched pending-form flow).
  const changeCollaboratorRole = async (utenteEntry, role) => {
    try {
      await dispatch(
        updateSchedaSpese({
          schedaId: scheda._id,
          titolo: scheda.titolo,
          roleUpdates: [{ mailId: utenteEntry._id, role }],
        })
      ).unwrap();
      await onRefreshSingle();
      toast.success(`${utenteEntry.email} is now ${role}`);
    } catch (error) {
      toast.error(`Error updating role: ${error.message}`);
    }
  };

  const removeCollaborator = async (utenteEntry) => {
    try {
      await dispatch(
        updateSchedaSpese({
          schedaId: scheda._id,
          titolo: scheda.titolo,
          removedEmails: [{ mail: utenteEntry.email, mailId: utenteEntry._id }],
        })
      ).unwrap();
      await onRefreshSingle();
      toast.success(`${utenteEntry.email} removed from the scheda`);
    } catch (error) {
      toast.error(`Error removing user: ${error.message}`);
    }
  };

  // Sorting handlers
  const onChangeOder = async (orderType) => {
    setOrderState((prevState) => {
      const newOrderState = Object.keys(prevState).reduce((acc, key) => {
        acc[key] =
          key === orderType
            ? prevState[key] === "desc"
              ? "asc"
              : "desc"
            : null;
        return acc;
      }, {});
      return newOrderState;
    });
  };

  const fetchData = async (param) => {
    setisDisabled(true);
    try {
      await dispatch(singleSchedaSpeseGet(param)).unwrap();
    } catch (error) {
      console.log(error);
    } finally {
      setisDisabled(false);
    }
  };

  // Effects
  useEffect(() => {
    const activeOrderType = Object.keys(orderState).find(
      (key) => orderState[key] !== null
    );
    if (!activeOrderType) return;

    const param = {
      schedaId: scheda._id,
      orderKind: activeOrderType,
      orderState: orderState[activeOrderType],
    };
    fetchData(param);
  }, [orderState]);

  return (
    <>
      <div className="scheda-detail-grid">
        {/* Hero: title, Shared/Private badge, total exposure, actions */}
        <div className="scheda-detail-hero">
          <SchedaHeader
            scheda={scheda}
            isEditing={isEditing}
            titolo={titolo}
            erroLength={erroLength}
            onLongPress={onLongPress}
            onChange={onChange}
            cancelEdit={cancelEdit}
            updateSchedataTitolo={updateSchedataTitolo}
            onShare={() => handleShow("shareModal")}
            onDelete={() => handleShow("deleteModal")}
          />
        </div>

        {/* Sidebar: Add note button on top of Collaborators, kept together so
            they stay adjacent regardless of the hero/chart/activity column's
            height (desktop only — stacks inline with everything else on mobile) */}
        <div className="scheda-detail-sidebar">
          <div className="mb-3">
            <div
              className="dashboard-btn-primary w-100"
              onClick={() => handleShow("creaNotaModal")}
            >
              <FaPlus size={12} />
              <p className="mb-0">Add note</p>
            </div>
          </div>

          {scheda.utentiList.length > 0 && (
            <ExpensesSummary
              scheda={scheda}
              expencersWithTotals={expencersWithTotals}
              maxExpencer={maxExpencer}
              variant="default"
              currentUserEmail={user?.email}
              onChangeRole={changeCollaboratorRole}
              onRemoveUser={removeCollaborator}
            />
          )}

          <DropzoneCard
            multiple={false}
            accept="image/png,image/jpeg,image/webp,application/pdf"
            hint="Scan a receipt or statement - drag & drop, or click to browse"
            onUpload={handleAttachmentUpload}
          />
        </div>

        {/* Spending over time */}
        <div className="scheda-detail-chart">
          <SpendingLineChart singleScheda={scheda} />
        </div>

        {/* Recent activity */}
        <div className="scheda-detail-activity dashboard-card p-3 mb-2">
          {scheda.notaSpese.length === 0 ? (
            <div className="empty-state">
              <div className="empty-state-icon empty-state-icon-illustration">
                <img src={noActivityIcon} alt="" />
              </div>
              <div
                className="dashboard-btn-primary"
                onClick={() => handleShow("creaNotaModal")}
              >
                <FaPlus size={12} />
                <p className="mb-0">Add note</p>
              </div>
            </div>
          ) : (
            <>
              {/* Sorting buttons */}
              <div className="d-flex flex-wrap mb-1">
                <SortButton
                  label="User"
                  orderKey="userOrder"
                  orderState={orderState}
                  onClick={onChangeOder}
                  disabled={isDisabled}
                />
                <SortButton
                  label="Category"
                  orderKey="categoryOrder"
                  orderState={orderState}
                  onClick={onChangeOder}
                  disabled={isDisabled}
                />
                <SortButton
                  label="Date"
                  orderKey="dateOrder"
                  orderState={orderState}
                  onClick={onChangeOder}
                  disabled={isDisabled}
                />
                <SortButton
                  label="Price"
                  orderKey="priceOrder"
                  orderState={orderState}
                  onClick={onChangeOder}
                  disabled={isDisabled}
                />
              </div>

              {/* Notes list */}
              <ul className="list-group mb-0">
                {scheda.notaSpese.map(
                  (notaSpesa) =>
                    notaSpesa && (
                      <NotaSpesaItem
                        key={notaSpesa._id}
                        notaSpesa={notaSpesa}
                        user={user}
                        categories={categorie}
                        onEdit={editNota}
                      />
                    )
                )}
              </ul>
            </>
          )}
        </div>
      </div>

      {/* Modals */}
      <div className="row">
        {/* Create Note Modal */}
        <Modal
          show={modalState.creaNotaModal}
          onHide={() => handleModalClose("creaNotaModal")}
          centered
          contentClassName="note-modal-content"
        >
          <Modal.Header closeButton className="note-modal-header">
            <div className="d-flex align-items-center gap-3">
              <div className="note-modal-icon">
                <FaPlus size={16} />
              </div>
              <div>
                <Modal.Title className="note-modal-title">Create note</Modal.Title>
                <p className="note-modal-subtitle mb-0">
                  Add a new note to {scheda.titolo}.
                </p>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body className="note-modal-body">
            <NotaSpeseFormAllNote
              onSuccess={() => handleModalClose("creaNotaModal")}
              schedaId={scheda._id}
            />
          </Modal.Body>
        </Modal>

        {/* Edit Note Modal */}
        <Modal
          show={modalState.editNotaModal}
          onHide={() => handleModalClose("editNotaModal")}
          centered
          contentClassName="note-modal-content"
        >
          <Modal.Header closeButton className="note-modal-header">
            <div className="d-flex align-items-center gap-3">
              <div className="note-modal-icon">
                <FaEdit size={15} />
              </div>
              <div>
                <Modal.Title className="note-modal-title">Edit note</Modal.Title>
                <p className="note-modal-subtitle mb-0">
                  Update this note in {scheda.titolo}.
                </p>
              </div>
            </div>
          </Modal.Header>
          <Modal.Body className="note-modal-body">
            <NotaSpeseFormAllNote
              onSuccess={() => handleModalClose("editNotaModal")}
              schedaId={scheda._id}
              notaToEdit={notaSpesaToEdit}
              beforeDelete={() => closeCreateEditModal(scheda._id)}
            />
          </Modal.Body>
        </Modal>

        {/* Share Modal */}
        <ShareModal
          show={modalState.shareModal}
          onHide={() => handleModalClose("shareModal")}
          scheda={scheda}
          user={user}
          utentiList={utentiList}
          isFormModified={isFormModified}
          onAddEmail={addEmail}
          onValidateEmail={validateEmail}
          onRemoveEmail={removeEmail}
          onRemoveSharedUser={removeSharedUser}
          onChangeUserRole={changeUserRole}
          getPendingRole={getPendingRole}
          onSubmit={editSharedUserSubmit}
          onClose={() => handleModalClose("shareModal")}
        />

        {/* Delete Scheda Modal */}
        <DeleteConfirmModal
          show={modalState.deleteModal}
          onHide={() => handleModalClose("deleteModal")}
          title={scheda.titolo}
          itemName={scheda.titolo}
          itemType="scheda"
          onConfirm={deleteSchedaFromDetail}
          onCancel={() => handleModalClose("deleteModal")}
        />

        {/* Delete Note Modal */}
        <DeleteConfirmModal
          show={modalState.deleteNotaModal}
          onHide={() => handleModalClose("deleteNotaModal")}
          title={notaSpesaToEdit?.testo}
          itemName={notaSpesaToEdit?.testo}
          itemType="note"
          onConfirm={() => deleteNotaSpeseAction(notaSpesaToEdit._id)}
          onCancel={() => handleModalClose("deleteNotaModal")}
        />

        {/* Receipt scan review - shown after DropzoneCard's Upload scans a file */}
        <ReceiptReviewModal
          show={showReceiptReview}
          onHide={handleReceiptReviewHide}
          scanResult={scanResult}
          schedaId={scheda._id}
          categories={categorie}
        />
      </div>
    </>
  );
}

export default SingleSchedaAllNoteNew;
